import { useCallback, useEffect, useMemo, useState } from 'react';
import { Check } from 'lucide-react';
import { useNavigate, useParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';

import { ProcessDTO, ProcessStepDTO } from '@/types/api-types';

import IconMobileLogo from '@/assets/svg/icon-add-mobile.svg?react';
import IconChevronDown from '@/assets/svg/icon-chevron-down.svg?react';
import IconCloseOut from '@/assets/svg/icon-close-out.svg?react';
import IconClose from '@/assets/svg/icon-close.svg?react';

import { cn } from '@/lib/utils';

import { useIsMobile } from '@/hooks/use-mobile';
import { usePrompt } from '@/hooks/usePrompt';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { AddStepDialog } from '@/components/AddStepDialog';
import { ConfirmationDialog } from '@/components/ConfirmationDialog/ConfirmationDialog';

import { useGlobalStore } from '@/stores/globalStore';

import { apiService } from '@/services/api-service';

import { PROCESS_TYPES, USER_ROLES } from '@/constants/global-constants';
import { adminRoutes, privateRoutes } from '@/constants/route-settings';

import { AddStepsTable } from './AddStepsTable';

export function AddOrCopyProcessTab() {
  const {
    getProcessByUuid,
    getProcessByIndexUuid,
    createProcess,
    processes,
    fetchProcesses,
    bulkSaveSteps,
    steps,
    editProcess,
  } = useGlobalStore();

  const { indexes, getIndexById } = useGlobalStore();

  const visibleIndexes = useMemo(() => {
    return indexes.filter((index) => !index.is_deleted);
  }, [indexes]);

  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [selectedIndexId, setSelectedIndexId] = useState<string | null>(null);
  const [dialogState, setDialogState] = useState({
    editingStepId: null as string | null,
    newStepId: null as string | null,
    deletingStepId: null as string | null,
  });
  const [dropdownState, setDropdownState] = useState({
    isOpen: false,
    searchValue: '',
  });

  const [isProcessExists, setIsProcessExists] = useState<ProcessDTO | false>(
    false,
  );

  const [tempSteps, setTempSteps] = useState<ProcessStepDTO[]>([]);

  const [isLoading, setIsLoading] = useState(false);

  const currentIndex = useMemo(
    () => (selectedIndexId ? getIndexById(selectedIndexId) : null),
    [selectedIndexId],
  );

  const { uuid: copyFromProcessId } = useParams<{ uuid: string }>();

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  usePrompt(hasUnsavedChanges);

  useEffect(() => {
    const init = async () => {
      const processId = copyFromProcessId;
      if (!processId || visibleIndexes.length === 0) return;

      let existingProcess = getProcessByUuid(processId);
      if (!existingProcess) {
        existingProcess = await apiService.getProcess(processId);
      }

      setTempStepsCallback(existingProcess);
    };

    if (copyFromProcessId && processes.length > 0) {
      init();
    }
    return () => {
      setHasUnsavedChanges(false);
    };
  }, [copyFromProcessId, processes.length]);

  const setTempStepsCallback = useCallback(
    (existingProcess: ProcessDTO) => {
      setTempSteps(
        steps
          .filter(
            (step) =>
              existingProcess.steps.includes(step.uuid) && !step.is_deleted,
          )
          .map((item) => ({ ...item, uuid: uuidv4() })),
      );
    },
    [steps],
  );

  useEffect(() => {
    if (processes.length === 0) {
      fetchProcesses();
    }
  }, [processes]);

  const handleAddNewStep = () => {
    setDialogState((prev) => ({ ...prev, newStepId: uuidv4() }));
  };

  const handleAddOrEditStep = async (data: Partial<ProcessStepDTO>) => {
    if (data.uuid) {
      setTempSteps((prev) =>
        prev.map((step) =>
          step.uuid === data.uuid ? (data as ProcessStepDTO) : step,
        ),
      );
    } else {
      setTempSteps((prev) => [
        ...prev,
        {
          status_name: '',
          days_until_deadline: 0,
          user_type: USER_ROLES.EXECUTOR.value,
          comment: '',
          process_set: [],
          created: new Date().toISOString(),
          modified: new Date().toISOString(),
          ...data,
          uuid: uuidv4(),
        },
      ]);
    }
    setDialogState((prev) => ({ ...prev, newStepId: null }));
    setHasUnsavedChanges(true);
  };

  const onConfirmDelete = () => {
    if (dialogState.deletingStepId) {
      setTempSteps((prev) =>
        prev.filter((step) => step.uuid !== dialogState.deletingStepId),
      );
      setDialogState((prev) => ({ ...prev, deletingStepId: null }));
      setHasUnsavedChanges(true);
      setDialogState((prev) => ({ ...prev, deletingStepId: null }));
    }
  };

  const handleSaveProcess = async () => {
    if (!currentIndex) return;
    setIsLoading(true);
    setHasUnsavedChanges(false);

    try {
      const newProcess: Omit<ProcessDTO, 'uuid'> = {
        created: new Date().toISOString(),
        modified: new Date().toISOString(),
        type: PROCESS_TYPES.ADDITIONAL,
        steps: [],
        name: `${currentIndex.name}-процес`,
        direction: currentIndex.insurance_area,
        object_type: currentIndex.insurance_object_type,
        index: currentIndex.uuid,
      };
      const createdProcess = await createProcess(newProcess);

      const newStepsWithProcessId: ProcessStepDTO[] = tempSteps
        .filter((step) => !step.is_deleted)
        .map((step) => ({
          ...step,
          processes: [createdProcess.uuid],
        }));

      const newStepsUuids = await bulkSaveSteps(newStepsWithProcessId);

      const updatedProcess: ProcessDTO = {
        ...createdProcess,
        steps: [...createdProcess.steps, ...newStepsUuids],
      };
      await editProcess(updatedProcess);

      navigate(
        `${privateRoutes[USER_ROLES.ADMIN.value].settings}/${
          privateRoutes[USER_ROLES.ADMIN.value].processes.list
        }`,
      );
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectIndex = async (indexUuid: string) => {
    const existingProcess = getProcessByIndexUuid(indexUuid);

    if (existingProcess) {
      setIsProcessExists(existingProcess);
      return;
    }

    setSelectedIndexId(indexUuid as string);
    setDropdownState((prev) => ({
      ...prev,
      searchValue: '',
      isOpen: false,
    }));
    setHasUnsavedChanges(true);
  };

  const filteredIndexes = useMemo(() => {
    return visibleIndexes.filter(
      (index) =>
        dropdownState.searchValue === '' ||
        `${index.name} - ${index.description}`
          .toLowerCase()
          .includes(dropdownState.searchValue.toLowerCase()),
    );
  }, [visibleIndexes, dropdownState.searchValue]);

  return (
    <>
      <ConfirmationDialog
        isOpen={!!dialogState.deletingStepId}
        onClose={() =>
          setDialogState((prev) => ({ ...prev, deletingStepId: null }))
        }
        onConfirm={onConfirmDelete}
        title="Видалити етап?"
        cancelText="Скасувати"
        confirmText="Видалити"
      />
      <ConfirmationDialog
        isOpen={!!isProcessExists}
        onClose={() => {
          setDropdownState((prev) => ({ ...prev, searchValue: '' }));
          setIsProcessExists(false);
        }}
        onConfirm={() =>
          navigate(
            `${privateRoutes[USER_ROLES.ADMIN.value].settings}/${privateRoutes[
              USER_ROLES.ADMIN.value
            ].processes.edit.replace(
              ':uuid',
              (isProcessExists as ProcessDTO).uuid,
            )}`,
          )
        }
        title="Процес для обраного індексу вже існує, перейти до редагування?"
        cancelText="Скасувати"
        confirmText="Перейти"
      />

      <AddStepDialog
        open={!!dialogState.newStepId}
        onClose={() => setDialogState((prev) => ({ ...prev, newStepId: null }))}
        onSubmit={handleAddOrEditStep}
        editingStep={null}
      />
      <AddStepDialog
        open={!!dialogState.editingStepId}
        onClose={() =>
          setDialogState((prev) => ({ ...prev, editingStepId: null }))
        }
        onSubmit={handleAddOrEditStep}
        editingStep={
          tempSteps.find((step) => step.uuid === dialogState.editingStepId) ||
          null
        }
      />
      <div className="flex h-[calc(100vh-150px)] flex-col overflow-hidden rounded-md bg-white shadow-sm sm:max-w-[1500px]">
        <div className="flex-shrink-0">
          <div className="flex justify-between pl-8 pr-5 pt-5">
            <div className="mb-6 flex flex-col gap-2">
              <h2 className="mt-1 text-2xl font-medium text-primary">
                {!!copyFromProcessId ? 'Копіювати процес' : 'Створити процес'}
              </h2>
              <p className="font-medium">
                {!!copyFromProcessId
                  ? 'Оберіть індекс для створення копії процесу'
                  : 'Для створення процесу оберіть Індекс'}
              </p>
            </div>
            <Button
              variant="ghost"
              onClick={() =>
                navigate(
                  `${adminRoutes.settings}/${adminRoutes.processes.list}`,
                )
              }
              className="h-[22px] w-[22px]"
            >
              <IconCloseOut className="h-[22px] w-[22px] fill-white hover:fill-primary" />
            </Button>
          </div>
          <div className="mb-2 flex items-center justify-between pl-4 pr-4 sm:pl-8 sm:pr-6">
            <div className="flex items-center gap-2.5">
              <Popover
                open={dropdownState.isOpen}
                onOpenChange={(open) =>
                  setDropdownState((prev) => ({ ...prev, isOpen: open }))
                }
              >
                <PopoverTrigger asChild>
                  <div className="relative z-20 mr-2 flex min-h-[50px] min-w-[60vw] items-center justify-start rounded-md border border-gray-200 px-4 pr-10 text-left text-gray-200 focus-visible:outline-none sm:min-w-[450px]">
                    {currentIndex ? (
                      <span className="text-base text-primary">
                        {`${currentIndex.name} - ${currentIndex.description}`}
                      </span>
                    ) : (
                      'Індекс'
                    )}
                    <button className="absolute left-0 top-0 -z-10 flex h-full w-full items-center justify-end pr-4 focus-visible:outline-none">
                      <IconChevronDown className="h-[10px] w-[20px] fill-transparent stroke-gray-200" />
                    </button>
                  </div>
                </PopoverTrigger>
                <PopoverContent className="relative w-full min-w-[200px] p-0 sm:min-w-[450px]">
                  <Command shouldFilter={false}>
                    <div className="relative flex items-center border-b border-separator">
                      <CommandInput
                        placeholder="Індекс"
                        value={dropdownState.searchValue}
                        onValueChange={(value) =>
                          setDropdownState((prev) => ({
                            ...prev,
                            searchValue: value,
                          }))
                        }
                        className="h-[50px] pr-10 text-base"
                      />
                      {dropdownState.searchValue && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDropdownState((prev) => ({
                              ...prev,
                              searchValue: '',
                            }));
                          }}
                          className="absolute right-5 top-1/2 -translate-y-1/2"
                        >
                          <IconClose className="h-4 w-4 fill-gray-200" />
                        </button>
                      )}
                    </div>

                    <CommandList>
                      {filteredIndexes.map((index) => (
                        <CommandItem
                          key={index.uuid}
                          value={index.uuid}
                          onSelect={() => handleSelectIndex(index.uuid)}
                          className="h-[50px]"
                        >
                          <Check
                            className={cn(
                              'h-4 w-4',
                              selectedIndexId === index.uuid
                                ? 'opacity-100'
                                : 'opacity-0',
                            )}
                          />
                          {index.name} - {index.description}
                        </CommandItem>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            {isMobile ? (
              <button
                className="rounded-md text-white [&:disabled_svg]:fill-gray-400"
                disabled={!selectedIndexId}
                onClick={handleAddNewStep}
              >
                <IconMobileLogo className="h-[34px] w-[34px] fill-primary" />
              </button>
            ) : (
              <Button
                variant="default"
                disabled={!selectedIndexId}
                onClick={handleAddNewStep}
              >
                Додати етап
              </Button>
            )}
          </div>
        </div>

        <div className="flex-grow overflow-auto">
          <AddStepsTable
            steps={tempSteps.filter((step) => !step.is_deleted)}
            isLoading={isLoading}
            onEdit={(id) =>
              setDialogState((prev) => ({ ...prev, editingStepId: id }))
            }
            onDelete={(id) =>
              setDialogState((prev) => ({ ...prev, deletingStepId: id }))
            }
            isNew={!copyFromProcessId}
          />
        </div>

        <div className="flex flex-shrink-0 justify-end p-6">
          <Button
            variant={!tempSteps.length ? 'default' : 'outline'}
            disabled={!tempSteps.length || !selectedIndexId}
            onClick={handleSaveProcess}
          >
            Зберегти
          </Button>
        </div>
      </div>
    </>
  );
}
